import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Table, Button, Form, Alert, Container, Row, Col, Card, Badge, Pagination } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSearch, FaUsers, FaUser, FaToggleOn, FaToggleOff, FaFilter } from "react-icons/fa";
import { useComensales } from "../../../services/hooks/useComensales";
import ModalComensal from "../../../components/modals/ModalComensal";
import { Modal } from "antd";

const MantenimientoComensales = () => {
  const navigate = useNavigate();
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));

  // Usar los hooks personalizados (sin cargar automáticamente todos los comensales)
  const {
    loading,
    createComensal,
    updateComensal,
    deleteComensal: deleteComensalApi,
    fetchComensalesByProyecto,
    toggleActivoComensal
  } = useComensales();

  const [state, setState] = useState({
    comensalSeleccionado: null,
    searchTerm: "",
    currentPage: 1,
    itemsPerPage: 10,
    comensalesDelProyecto: [],
    soloActivos: false
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' o 'edit'
  const [comensalToEdit, setComensalToEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [comensalToDelete, setComensalToDelete] = useState(null);

  // Función estable para cargar comensales del proyecto
  const loadComensalesDelProyecto = useCallback(async () => {
    if (selectedProject?.id) {
      try {
        const comensalesProyecto = await fetchComensalesByProyecto(selectedProject.id);
        setState(prev => ({
          ...prev,
          comensalesDelProyecto: Array.isArray(comensalesProyecto) ? comensalesProyecto : []
        }));
      } catch (error) {
        console.error("Error al cargar comensales del proyecto:", error);
        setState(prev => ({ ...prev, comensalesDelProyecto: [] }));
      }
    }
  }, [selectedProject?.id, fetchComensalesByProyecto]);

  // Cargar comensales del proyecto seleccionado
  useEffect(() => {
    loadComensalesDelProyecto();
  }, [loadComensalesDelProyecto]);

  const handleSearch = (event) => {
    setState(prev => ({ ...prev, searchTerm: event.target.value }));
  };

  const handleComensalSelect = (comensal) => {
    setState(prev => ({ 
      ...prev, 
      comensalSeleccionado: prev.comensalSeleccionado?.id === comensal.id ? null : comensal 
    }));
  };

  const handleDelete = (id) => {
    if (!id) {
      toast.error("Por favor seleccione un comensal para eliminar", {
        position: "top-right",
        autoClose: 2000
      });
      return;
    }

    setComensalToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!comensalToDelete) return;

    // Cerrar modal inmediatamente
    setShowDeleteModal(false);
    
    try {
      await deleteComensalApi(comensalToDelete);
      
      // Recargar comensales del proyecto después de eliminar
      await loadComensalesDelProyecto();
      setState(prev => ({ ...prev, comensalSeleccionado: null })); // Limpiar selección
    } catch (error) {
      console.error("Error al eliminar comensal:", error);
      // El error ya se maneja en el hook con toast
    } finally {
      setComensalToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setComensalToDelete(null);
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setComensalToEdit(null);
    setShowModal(true);
  };

  const handleOpenEditModal = () => {
    if (state.comensalSeleccionado) {
      setModalMode('edit');
      setComensalToEdit(state.comensalSeleccionado);
      setShowModal(true);
    } else {
      toast.error("Por favor seleccione un comensal para editar", {
        position: "top-right",
        autoClose: 2000
      });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setComensalToEdit(null);
  };

  const handleSaveComensal = async (comensalData) => {
    try {
      if (modalMode === 'create') {
        await createComensal(comensalData, false); // No mostrar toast automático
      } else {
        await updateComensal(comensalToEdit.id, comensalData);
      }
      
      // Recargar comensales del proyecto después de guardar
      await loadComensalesDelProyecto();
      
      // Cerrar el modal
      handleCloseModal();
    } catch (error) {
      console.error('Error al guardar comensal:', error);
      // El error ya se maneja en el hook con toast
    }
  };

  // Filtrar comensales del proyecto según el término de búsqueda y estado activo
  const filteredComensales = state.comensalesDelProyecto.filter((comensal) => {
    // Filtro por término de búsqueda
    const matchesSearch = comensal.nombre?.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
      comensal.numero_documento_identidad?.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
      comensal.numero_documento_comedor?.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
      comensal.empresa?.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
      comensal.cargo?.toLowerCase().includes(state.searchTerm.toLowerCase());
    
    // Filtro por estado activo
    const matchesActivo = !state.soloActivos || comensal.activo === 1;
    
    return matchesSearch && matchesActivo;
  });

  // Implementar paginado
  const totalItems = filteredComensales.length;
  const totalPages = Math.ceil(totalItems / state.itemsPerPage);
  const startIndex = (state.currentPage - 1) * state.itemsPerPage;
  const endIndex = startIndex + state.itemsPerPage;
  const paginatedComensales = filteredComensales.slice(startIndex, endIndex);

  // Manejar cambio de página
  const handlePageChange = (pageNumber) => {
    setState(prev => ({ ...prev, currentPage: pageNumber }));
  };

  // Resetear página cuando se cambia la búsqueda
  const handleSearchWithReset = (event) => {
    setState(prev => ({ 
      ...prev, 
      searchTerm: event.target.value, 
      currentPage: 1 
    }));
  };

  // Manejar toggle de estado activo
  const handleToggleActivo = async (comensal) => {
    try {
      await toggleActivoComensal(comensal.id);
      // Recargar comensales del proyecto después del toggle
      await loadComensalesDelProyecto();
    } catch (error) {
      console.error("Error al cambiar estado del comensal:", error);
    }
  };

  // Manejar filtro de solo activos
  const handleToggleSoloActivos = () => {
    setState(prev => ({ 
      ...prev, 
      soloActivos: !prev.soloActivos,
      currentPage: 1 
    }));
  };

  return (
    <>
      {/* Digital Harmony Styles */}
      <style>
        {`
          /* Fondo degradado */
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.2rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
            margin-bottom: 2rem;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            font-weight: 700;
          }
          
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-warning {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(251, 191, 36, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(251, 191, 36, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          /* Campos de formulario modernos */
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          
          /* Enhanced Table */
          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }
          
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.875rem !important;
            text-transform: uppercase !important;
            padding: 1rem 0.75rem !important;
            border: none !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }
          
          .dh-enhanced-table tbody td {
            padding: 0.75rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
            vertical-align: middle;
          }
          
          .dh-enhanced-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%) !important;
          }
          
          .dh-enhanced-table tbody tr.selected {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%) !important;
            border-left: 4px solid #667eea;
          }
          
          /* Badge moderno */
          .dh-badge {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 10px;
            padding: 8px 12px;
            font-weight: 600;
            font-size: 0.85rem;
          }
          
          /* Loading spinner personalizado */
          .dh-spinner {
            display: inline-block;
            width: 2rem;
            height: 2rem;
            border: 0.25rem solid rgba(102, 126, 234, 0.2);
            border-radius: 50%;
            border-top-color: #667eea;
            animation: spin 1s ease-in-out infinite;
          }
          
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          
          /* Alert personalizado */
          .dh-alert {
            border-radius: 15px;
            padding: 1rem 1.5rem;
            border: none;
            margin-bottom: 1.5rem;
          }
          
          .dh-alert-info {
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 197, 253, 0.1) 100%);
            color: #1e40af;
            border-left: 4px solid #3b82f6;
          }
          
          .dh-alert-warning {
            background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%);
            color: #92400e;
            border-left: 4px solid #f59e0b;
          }

          /* Paginación personalizada */
          .dh-pagination {
            justify-content: center;
            margin-top: 2rem;
          }
          
          .dh-pagination .page-item .page-link {
            border: none;
            background: white;
            color: #6b7280;
            font-weight: 600;
            border-radius: 8px;
            margin: 0 4px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            transition: all 0.3s ease;
          }
          
          .dh-pagination .page-item .page-link:hover {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
          }
          
          .dh-pagination .page-item.active .page-link {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
          }
          
          /* Info de paginación */
          .dh-pagination-info {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 1rem;
            color: #6b7280;
            font-size: 0.9rem;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer />
        
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dh-main-title">
                  <FaUsers className="me-3" />
                  Mantenimiento de Comensales
                </h1>
                <p className="dh-main-subtitle">Gestión completa de comensales del sistema</p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <Badge className="dh-badge">
                  Proyecto: {selectedProject?.nombre || "No seleccionado"}
                </Badge>
                <Button 
                  className="btn-dh-secondary d-flex align-items-center"
                  onClick={() => navigate("/home")}
                >
                  <FaArrowLeft className="me-2" />
                  Volver
                </Button>
              </div>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row>
            <Col lg={12}>
              {/* Verificar que hay proyecto seleccionado */}
              {!selectedProject?.id ? (
                <Alert variant="warning" className="dh-alert dh-alert-warning text-center">
                  <FaUsers className="me-2" />
                  No hay proyecto seleccionado. Por favor, seleccione un proyecto desde el menú principal para ver los comensales asociados.
                </Alert>
              ) : (
                <Card className="dh-card">
                  <Card.Header className="dh-card-header dh-card-header-primary">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">
                        <FaUser className="me-2" />
                        Comensales del Proyecto: {selectedProject.nombre}
                      </h5>
                    <div className="d-flex gap-2">
                      <Button
                        className="btn-dh-primary d-flex align-items-center"
                        onClick={handleOpenCreateModal}
                        title="Agregar nuevo comensal"
                      >
                        <FaPlus className="me-2" size={14} />
                        Nuevo
                      </Button>
                      <Button
                        className="btn-dh-warning d-flex align-items-center"
                        onClick={handleOpenEditModal}
                        disabled={!state.comensalSeleccionado}
                        title="Editar comensal seleccionado"
                      >
                        <FaEdit className="me-2" size={14} />
                        Editar
                      </Button>
                      <Button
                        className="btn-dh-danger d-flex align-items-center"
                        onClick={() => handleDelete(state.comensalSeleccionado?.id)}
                        disabled={!state.comensalSeleccionado}
                        title="Eliminar comensal seleccionado"
                      >
                        <FaTrash className="me-2" size={14} />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body style={{ padding: '2rem' }}>
                  {/* Barra de búsqueda y filtros */}
                  <div className="mb-4">
                    <Row className="align-items-center">
                      <Col md={6}>
                        <div className="position-relative">
                          <Form.Control
                            type="text"
                            className="dh-form-control"
                            placeholder="Buscar comensal..."
                            value={state.searchTerm}
                            onChange={handleSearchWithReset}
                            style={{ paddingLeft: '3rem' }}
                          />
                          <FaSearch 
                            className="position-absolute" 
                            style={{ 
                              left: '1rem', 
                              top: '50%', 
                              transform: 'translateY(-50%)', 
                              color: '#6b7280' 
                            }} 
                          />
                        </div>
                      </Col>
                      <Col md={6} className="text-end">
                        <Button
                          variant={state.soloActivos ? 'success' : 'outline-secondary'}
                          onClick={handleToggleSoloActivos}
                          className="d-flex align-items-center"
                          style={{
                            borderRadius: '8px',
                            padding: '0.5rem 1rem',
                            fontSize: '0.9rem',
                            fontWeight: '600'
                          }}
                        >
                          <FaFilter className="me-2" />
                          {state.soloActivos ? 'Mostrando solo activos' : 'Mostrar solo activos'}
                        </Button>
                      </Col>
                    </Row>
                  </div>

                  {/* Tabla de datos */}
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="dh-spinner"></div>
                      <p className="mt-3 text-muted">Cargando comensales...</p>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table className="dh-enhanced-table">
                          <thead>
                            <tr>
                              <th style={{ width: "50px" }}></th>
                              <th>Cliente</th>
                              <th>Nombre Completo</th>
                              <th>Doc. Identidad</th>
                              <th>Doc. Comedor</th>
                              <th>Cargo</th>
                              <th style={{ width: "100px" }}>Estado</th>
                              <th style={{ width: "90px" }}>Dup.</th>
                              <th style={{ width: "120px" }}>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedComensales.length === 0 ? (
                              <tr>
                                <td colSpan="9" className="text-center py-4">
                                  <div className="dh-alert dh-alert-info">
                                    <FaUsers className="me-2" />
                                    {filteredComensales.length === 0 
                                      ? "No hay comensales registrados o no coinciden con la búsqueda"
                                      : "No hay comensales en esta página"
                                    }
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              paginatedComensales.map((comensal) => (
                                <tr 
                                  key={comensal.id}
                                  className={state.comensalSeleccionado?.id === comensal.id ? 'selected' : ''}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleComensalSelect(comensal)}
                                >
                                  <td>
                                    <div className="form-check">
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={state.comensalSeleccionado?.id === comensal.id}
                                        onChange={() => {}}
                                      />
                                    </div>
                                  </td>
                                  <td style={{ fontWeight: '600', color: '#2563eb' }}>
                                    {comensal.cliente?.nombre || comensal.nombre_cliente || 'Sin cliente'}
                                  </td>
                                  <td style={{ fontWeight: '600', color: '#374151' }}>
                                    {comensal.nombre || 'Sin nombre'}
                                  </td>
                                  <td>{comensal.numero_documento_identidad || 'N/A'}</td>
                                  <td>{comensal.numero_documento_comedor || 'N/A'}</td>
                                  <td>{comensal.cargo || 'N/A'}</td>
                                  <td>
                                    <Badge 
                                      bg={comensal.activo === 1 ? 'success' : 'secondary'}
                                      style={{ 
                                        fontSize: '0.75rem',
                                        padding: '0.4rem 0.8rem',
                                        borderRadius: '8px'
                                      }}
                                    >
                                      {comensal.activo === 1 ? 'Activo' : 'Inactivo'}
                                    </Badge>
                                  </td>
                                  <td>
                                    {comensal.permite_duplicados_atenciones ? (
                                      <Badge bg="warning" text="dark" title="Permite atenciones duplicadas">Sí</Badge>
                                    ) : (
                                      <Badge bg="light" text="dark">No</Badge>
                                    )}
                                  </td>
                                  <td>
                                    <Button
                                      size="sm"
                                      variant={comensal.activo === 1 ? 'outline-danger' : 'outline-success'}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleActivo(comensal);
                                      }}
                                      style={{
                                        borderRadius: '8px',
                                        padding: '0.4rem 0.8rem',
                                        fontSize: '0.75rem',
                                        fontWeight: '600'
                                      }}
                                      title={comensal.activo === 1 ? 'Desactivar comensal' : 'Activar comensal'}
                                    >
                                      {comensal.activo === 1 ? (
                                        <>
                                          <FaToggleOff className="me-1" />
                                          Desactivar
                                        </>
                                      ) : (
                                        <>
                                          <FaToggleOn className="me-1" />
                                          Activar
                                        </>
                                      )}
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>

                      {/* Paginación y estadísticas */}
                      {filteredComensales.length > 0 && (
                        <>
                          <Pagination className="dh-pagination">
                            <Pagination.First 
                              onClick={() => handlePageChange(1)}
                              disabled={state.currentPage === 1}
                            />
                            <Pagination.Prev 
                              onClick={() => handlePageChange(state.currentPage - 1)}
                              disabled={state.currentPage === 1}
                            />
                            
                            {[...Array(totalPages)].map((_, index) => {
                              const pageNumber = index + 1;
                              return (
                                <Pagination.Item
                                  key={pageNumber}
                                  active={pageNumber === state.currentPage}
                                  onClick={() => handlePageChange(pageNumber)}
                                >
                                  {pageNumber}
                                </Pagination.Item>
                              );
                            })}
                            
                            <Pagination.Next 
                              onClick={() => handlePageChange(state.currentPage + 1)}
                              disabled={state.currentPage === totalPages}
                            />
                            <Pagination.Last 
                              onClick={() => handlePageChange(totalPages)}
                              disabled={state.currentPage === totalPages}
                            />
                          </Pagination>

                          <div className="dh-pagination-info">
                            <span>
                              Mostrando {startIndex + 1} a {Math.min(endIndex, totalItems)} de {totalItems} comensales
                            </span>
                            <span>
                              Página {state.currentPage} de {totalPages}
                            </span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <ModalComensal
        show={showModal}
        onHide={handleCloseModal}
        onSave={handleSaveComensal}
        comensal={comensalToEdit}
      />

      <Modal
        title={
          <span style={{ color: '#dc2626', fontWeight: '600' }}>
            <FaTrash className="me-2" />
            Confirmar Eliminación
          </span>
        }
        open={showDeleteModal}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Eliminar"
        cancelText="Cancelar"
        okType="danger"
        centered
        okButtonProps={{
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            border: 'none',
            borderRadius: '8px',
            fontWeight: '600'
          }
        }}
        cancelButtonProps={{
          style: {
            background: 'linear-gradient(135deg, #6b7280 0%, #374151 100%)',
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            fontWeight: '600'
          }
        }}
      >
        <div style={{ padding: '1rem 0' }}>
          <p style={{ color: '#374151', marginBottom: '0.5rem' }}>
            ¿Está seguro de que desea eliminar este comensal?
          </p>
          <p style={{ color: '#6b7280', marginBottom: '0', fontSize: '0.9rem' }}>
            Esta acción no se puede deshacer.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default MantenimientoComensales;